import { useCallback, useEffect, useState } from "react";
import { Loader2, PackageCheck, Printer, Truck } from "lucide-react";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/pages/Leads";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const EWAY_STATES = ["Not Required", "Draft", "Pending", "Generated", "Failed", "Cancelled",
  "Expired"];

/** Part dispatch: pick how much of each invoice line is going out on this trip. */
export function PartialDispatchDialog({ invoice, onClose, onCreated }) {
  const [plan, setPlan] = useState(null);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/dispatch-plan`);
      setPlan(data);
      setQty(Object.fromEntries(data.lines.filter((l) => l.stock_item_id)
        .map((l) => [l.stock_item_id, String(l.suggested_qty || 0)])));
    } catch (e) {
      toast.error(apiError(e));
      onClose();
    }
  }, [invoice.id, onClose]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const lines = (plan?.lines || [])
      .filter((l) => l.stock_item_id && Number(qty[l.stock_item_id]) > 0)
      .map((l) => ({ stock_item_id: l.stock_item_id, name: l.name, unit: l.unit,
        quantity: Number(qty[l.stock_item_id]) }));
    if (lines.length === 0) return toast.error("Enter the quantity going out now");
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/challans`, { lines });
      toast.success(`Challan ${data.number} created — stock moves only on dispatch`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        data-testid="partial-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>Part dispatch — {invoice.number}</DialogTitle>
          <DialogDescription>
            Send what is ready today and keep the rest pending. A draft challan does not reduce
            stock — stock moves out only when you confirm the dispatch.
          </DialogDescription>
        </DialogHeader>
        {!plan ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Invoiced</TableHead>
                    <TableHead className="text-right">Already sent</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                    <TableHead className="text-right">In stock</TableHead>
                    <TableHead className="text-right">Send now</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.lines.map((l, i) => (
                    <TableRow key={`${l.name}-${i}`} data-testid={`dispatch-line-${i}`}>
                      <TableCell className="text-sm">
                        {l.name}
                        {!l.in_stock && (
                          <Badge variant="destructive" className="ml-1 text-[9px]">
                            not in inventory
                          </Badge>
                        )}
                        {l.reserved_qty > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            {l.reserved_qty} {l.unit} on an open challan
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{l.ordered_qty}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{l.invoiced_qty}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{l.dispatched_qty}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{l.remaining_qty}</TableCell>
                      <TableCell className="text-right font-mono-num text-xs">{l.available_stock}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          className="ml-auto h-8 w-20 text-right"
                          data-testid={`dispatch-qty-input-${i}`}
                          disabled={!l.in_stock || l.remaining_qty <= 0}
                          value={qty[l.stock_item_id] ?? ""}
                          onChange={(e) => setQty({ ...qty, [l.stock_item_id]: e.target.value })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {plan.fully_dispatched && (
              <p className="text-xs text-muted-foreground" data-testid="fully-dispatched-note">
                Everything on this invoice has already been dispatched.
              </p>
            )}
          </>
        )}
        <DialogFooter>
          <Button disabled={busy || !plan} onClick={create} data-testid="create-part-challan">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Create challan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Record the officially generated e-way bill and where it stands. */
export function EwayStatusDialog({ challan, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: challan.eway_status || (challan.eway_bill ? "Generated" : "Draft"),
    eway_number: challan.eway_number || challan.eway_bill || "",
    eway_date: challan.eway_date || "",
    valid_till: challan.eway_valid_till || "",
    remarks: challan.eway_remarks || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/challans/${challan.id}/eway-status`, form);
      toast.success(`E-way bill marked ${form.status}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="eway-status-dialog">
        <DialogHeader>
          <DialogTitle>E-way bill — {challan.number}</DialogTitle>
          <DialogDescription>
            Pramsia never creates an e-way bill number. Generate it on the government portal, then
            record the official number here.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="eway-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EWAY_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Official e-way bill number">
            <Input data-testid="eway-number-input" value={form.eway_number}
              onChange={(e) => setForm({ ...form, eway_number: e.target.value })}
              placeholder="12 digit number from the portal" />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Generated on">
              <Input type="date" data-testid="eway-date-input" value={form.eway_date}
                onChange={(e) => setForm({ ...form, eway_date: e.target.value })} />
            </Field>
            <Field label="Valid till">
              <Input type="date" data-testid="eway-valid-input" value={form.valid_till}
                onChange={(e) => setForm({ ...form, valid_till: e.target.value })} />
            </Field>
          </div>
          <Field label="Remarks">
            <Input data-testid="eway-remarks-input" value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button disabled={busy} onClick={save} data-testid="eway-status-save">
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save e-way status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DispatchPackButton({ challan }) {
  return (
    <Button size="sm" variant="outline" className="gap-1"
      data-testid={`dispatch-pack-button-${challan.id}`}
      onClick={() => window.open(`${API}/challans/${challan.id}/dispatch-pack.pdf`, "_blank")}>
      <Printer className="h-3.5 w-3.5" /> Pack
    </Button>
  );
}

export function ConfirmDispatchButton({ challan, onDone }) {
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState(null);

  const run = async (acknowledged = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challans/${challan.id}/confirm-dispatch`,
        { eway_acknowledged: acknowledged },
        { headers: { "Idempotency-Key": `dispatch-${challan.id}` } });
      toast.success(data.already_dispatched || data.idempotent_replay
        ? "This challan was already dispatched"
        : "Dispatched — stock updated once");
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "eway_missing") setWarning(detail);
      else toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" className="gap-1" disabled={busy}
        data-testid={`confirm-dispatch-button-${challan.id}`} onClick={() => run(false)}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Truck className="h-3.5 w-3.5" />}
        Dispatch
      </Button>
      <Dialog open={!!warning} onOpenChange={(o) => !o && setWarning(null)}>
        <DialogContent data-testid="confirm-eway-warning">
          <DialogHeader>
            <DialogTitle>E-way bill not recorded</DialogTitle>
            <DialogDescription>{warning?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="confirm-eway-later"
              onClick={() => setWarning(null)}>
              I'll record the e-way bill
            </Button>
            <Button variant="destructive" data-testid="confirm-eway-anyway"
              onClick={() => { setWarning(null); run(true); }}>
              <PackageCheck className="mr-1 h-4 w-4" /> Dispatch without it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
